import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Group, Loader, ScrollArea, Stack, Table, Text } from '@mantine/core';
import ImportDraftService, { errorMessage, isApiError } from '@/services/import-draft/import-draft-service';
import { ImportRecipe, SourceSample } from '@/services/import-draft/import-draft-model';

interface Props {
  draftID: string;
  recipe: ImportRecipe;
  rows?: number;
}

export default function SourceSamplePanel({ draftID, recipe, rows = 8 }: Props) {
  const [sample, setSample] = useState<SourceSample | null>(null);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ImportDraftService.sourceSample(draftID, rows, skip).then(result => {
      if (cancelled) return;
      if (isApiError(result)) setError(errorMessage(result, 'The file could not be read.'));
      else {
        setError(null);
        setSample(result as SourceSample);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [draftID, rows, skip]);

  const targetFor = (columnIndex: number) => recipe.Fields.filter(f => f.Sources.includes(columnIndex)).map(f => f.Target);

  if (error) return <Alert color="red">{error}</Alert>;
  if (!sample) return <Group justify="center" p="md"><Loader size="sm" /></Group>;

  const first = sample.Rows[0]?.RowIndex;
  const last = sample.Rows[sample.Rows.length - 1]?.RowIndex;

  return (
    <Stack gap="xs" data-testid="source-sample">
      <Text size="xs" c="dimmed">
        {sample.SheetName}: {sample.TotalRows.toLocaleString()} rows, {sample.ColumnCount} columns. Showing rows {first !== undefined ? first + 1 : '-'} to {last !== undefined ? last + 1 : '-'}. Badges show the ServCraft field each column maps to.
      </Text>
      <ScrollArea type="auto" offsetScrollbars w="100%" style={{ maxWidth: '100%' }}>
        <Table fz="xs" verticalSpacing={4} withTableBorder withColumnBorders striped miw={sample.ColumnCount * 150}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Row</Table.Th>
              {sample.Headers.map((h, i) => {
                const targets = targetFor(i);
                return (
                  <Table.Th key={i} style={{ verticalAlign: 'top' }}>
                    <Text size="xs" fw={600}>{h}</Text>
                    <Text size="xs" c="dimmed">column {i + 1}</Text>
                    {targets.map(t => <Badge key={t} size="xs" variant="light" mr={4}>{t}</Badge>)}
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sample.Rows.map(row => (
              <Table.Tr key={row.RowIndex}>
                <Table.Td c="dimmed">{row.RowIndex + 1}</Table.Td>
                {row.Cells.map((cell, i) => <Table.Td key={i} style={{ whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }} title={cell}>{cell}</Table.Td>)}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      <Group gap="xs">
        <Button size="xs" variant="subtle" disabled={skip === 0 || loading} onClick={() => setSkip(Math.max(0, skip - rows))}>Previous rows</Button>
        <Button size="xs" variant="subtle" disabled={loading || sample.Rows.length < rows} onClick={() => setSkip(skip + rows)}>Next rows</Button>
        {loading && <Loader size="xs" />}
      </Group>
    </Stack>
  );
}
